import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Eye, EyeOff, Pencil, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatCurrency } from "@/lib/crm";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/produtos")({
  component: ProdutosPage,
});

type Product = {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  price: string;
  cost: string;
  durationMinutes: number | null;
  active: boolean;
  soldCount: number;
};

/**
 * Catálogo de produtos e serviços.
 *
 * Nada aqui é conhecimento do sistema: "Botox" é uma linha da tabela do
 * cliente. A duração em minutos é o que separa vender um creme de agendar uma
 * aplicação — item sem duração não ocupa agenda.
 *
 * Uma tabela só, ordenada por categoria, em vez de uma tabela por categoria:
 * com três categorias de dois itens cada, três cabeçalhos ocupavam mais tela
 * do que os itens.
 */
function ProdutosPage() {
  const { can } = useAuth();
  const canEdit = can("admin", "manager");
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [dialogo, setDialogo] = useState<Product | "novo" | null>(null);
  const [desativando, setDesativando] = useState<Product | null>(null);

  const products = useQuery({
    queryKey: ["products", showInactive],
    queryFn: () =>
      api.get<Product[]>("/products", { includeInactive: showInactive ? "true" : undefined }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["products"] });
  const fail = (e: unknown) =>
    toast.error(e instanceof ApiError ? e.message : "Não foi possível concluir");

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post("/products", body),
    onSuccess: async () => {
      toast.success("Serviço criado.");
      await refresh();
    },
    onError: fail,
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/products/${id}`, body),
    onSuccess: refresh,
    onError: fail,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSuccess: async () => {
      toast.success("Serviço desativado.");
      await refresh();
    },
    onError: fail,
  });

  const itens = [...(products.data ?? [])].sort(
    (a, b) =>
      (a.category ?? "￿").localeCompare(b.category ?? "￿", "pt-BR") ||
      a.name.localeCompare(b.name, "pt-BR"),
  );

  const salvar = (body: Record<string, unknown>) => {
    if (dialogo === "novo") {
      create.mutate(body, { onSuccess: () => setDialogo(null) });
    } else if (dialogo) {
      patch.mutate(
        { id: dialogo.id, body },
        {
          onSuccess: () => {
            toast.success("Serviço atualizado.");
            setDialogo(null);
          },
        },
      );
    }
  };

  return (
    <>
      <PageHeader
        title="Serviços"
        description="O que a empresa vende, com preço e custo"
        action={
          <>
            <Button
              variant={showInactive ? "soft" : "outline"}
              size="sm"
              aria-pressed={showInactive}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive ? <EyeOff /> : <Eye />}
              {showInactive ? "Ocultar desativados" : "Mostrar desativados"}
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => setDialogo("novo")}>
                <Plus />
                Novo serviço
              </Button>
            )}
          </>
        }
      />

      <PageBody>
        <Card>
          <CardContent className="px-0 pb-0 pt-0">
            {products.isLoading ? (
              <div className="space-y-2 p-5">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-11 w-full" />
                ))}
              </div>
            ) : itens.length === 0 ? (
              <EmptyState
                icon={Sparkles}
                title="Nenhum serviço no catálogo"
                description="Cadastre o que a clínica vende — com preço, custo e duração — para registrar vendas e ocupar a agenda."
                action={
                  canEdit ? (
                    <Button variant="outline" size="sm" onClick={() => setDialogo("novo")}>
                      <Plus />
                      Novo serviço
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[48rem] text-sm">
                  <thead className="bg-muted/60 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-5 py-2.5 text-left">Serviço</th>
                      <th className="px-4 py-2.5 text-left">Categoria</th>
                      <th className="px-4 py-2.5 text-right">Preço</th>
                      <th className="px-4 py-2.5 text-right">Custo</th>
                      <th className="px-4 py-2.5 text-right">Margem</th>
                      <th className="px-4 py-2.5 text-right">Vendas</th>
                      <th className="px-4 py-2.5 text-left">Situação</th>
                      {canEdit && <th className="px-5 py-2.5" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {itens.map((item) => {
                      const preco = Number(item.price);
                      const custo = Number(item.cost);
                      const margem = preco > 0 ? Math.round(((preco - custo) / preco) * 100) : null;
                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            "transition-colors hover:bg-muted/40",
                            !item.active && "opacity-60",
                          )}
                        >
                          <td className="px-5 py-2.5">
                            <p className="max-w-[18rem] truncate font-medium" title={item.name}>
                              {item.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {item.durationMinutes
                                ? `${item.durationMinutes} min de agenda`
                                : "não ocupa agenda"}
                            </p>
                          </td>
                          <td className="px-4 py-2.5">
                            {item.category ? (
                              <Badge variant="muted">{item.category}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="kpi px-4 py-2.5 text-right font-semibold">
                            {formatCurrency(preco)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {formatCurrency(custo)}
                          </td>
                          <td
                            className={cn(
                              "px-4 py-2.5 text-right tabular-nums",
                              margem !== null && margem < 0 && "font-medium text-destructive",
                            )}
                          >
                            {margem === null ? "—" : `${margem}%`}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                            {item.soldCount}
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge variant={item.active ? "success" : "muted"}>
                              {item.active ? "Ativo" : "Desativado"}
                            </Badge>
                          </td>
                          {canEdit && (
                            <td className="px-5 py-2.5">
                              <div className="flex items-center justify-end gap-0.5">
                                <Hint label="Editar">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={`Editar ${item.name}`}
                                    className="text-muted-foreground"
                                    onClick={() => setDialogo(item)}
                                  >
                                    <Pencil />
                                  </Button>
                                </Hint>
                                {item.active ? (
                                  <Hint label="Desativar">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      aria-label={`Desativar ${item.name}`}
                                      className="text-muted-foreground hover:text-destructive"
                                      onClick={() => setDesativando(item)}
                                    >
                                      <Trash2 />
                                    </Button>
                                  </Hint>
                                ) : (
                                  <Hint label="Reativar">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      aria-label={`Reativar ${item.name}`}
                                      className="text-muted-foreground"
                                      disabled={patch.isPending}
                                      onClick={() =>
                                        patch.mutate({ id: item.id, body: { active: true } })
                                      }
                                    >
                                      <RotateCcw />
                                    </Button>
                                  </Hint>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {/* Montado só enquanto aberto, com uma `key` por item: o formulário lê o
          serviço na montagem, e é assim que abrir outro não herda os campos. */}
      {dialogo && (
        <ProductDialog
          key={dialogo === "novo" ? "novo" : dialogo.id}
          produto={dialogo}
          pending={create.isPending || patch.isPending}
          onClose={() => setDialogo(null)}
          onSubmit={salvar}
        />
      )}

      <Dialog open={desativando !== null} onOpenChange={(open) => !open && setDesativando(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desativar “{desativando?.name}”?</DialogTitle>
            <DialogDescription>
              {/* Desativar, não apagar: o relatório por serviço precisa da
                  linha continuar existindo. */}
              Ele some da lista e dos formulários de venda, mas as vendas já registradas continuam
              contando. Dá para reativar depois.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDesativando(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deactivate.isPending}
              onClick={() => {
                if (!desativando) return;
                deactivate.mutate(desativando.id, { onSuccess: () => setDesativando(null) });
              }}
            >
              {deactivate.isPending ? "Desativando…" : "Desativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Criar ou editar um serviço.
 *
 * Um formulário só para os dois casos: os campos são os mesmos, e o servidor
 * aceita atualização parcial — mandar tudo de novo é o jeito mais simples de
 * garantir que a tela e o banco dizem a mesma coisa depois de salvar.
 */
function ProductDialog({
  produto,
  pending,
  onClose,
  onSubmit,
}: {
  produto: Product | "novo";
  pending: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const editando = produto !== "novo";
  const [name, setName] = useState(editando ? produto.name : "");
  const [category, setCategory] = useState(editando ? (produto.category ?? "") : "");
  const [description, setDescription] = useState(editando ? (produto.description ?? "") : "");
  const [price, setPrice] = useState(editando ? produto.price : "");
  const [cost, setCost] = useState(editando ? produto.cost : "");
  const [duration, setDuration] = useState(
    editando && produto.durationMinutes ? String(produto.durationMinutes) : "",
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({
              name: name.trim(),
              category: category.trim() || null,
              description: description.trim() || null,
              price: Number(price) || 0,
              cost: Number(cost) || 0,
              durationMinutes: duration ? Number(duration) : null,
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>{editando ? "Editar serviço" : "Novo serviço"}</DialogTitle>
            <DialogDescription>
              O custo alimenta a margem real, não só a receita. Com minutos preenchidos, o serviço
              passa a ocupar a agenda.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-5 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="product-name">Nome</Label>
              <Input
                id="product-name"
                required
                minLength={2}
                maxLength={120}
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Limpeza de pele"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-category">Categoria</Label>
              <Input
                id="product-category"
                maxLength={80}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Facial"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-duration">Minutos de agenda</Label>
              <Input
                id="product-duration"
                type="number"
                min={1}
                max={1440}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Vazio = não ocupa agenda"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-price">Preço (R$)</Label>
              <Input
                id="product-price"
                type="number"
                min={0}
                step="0.01"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-cost">Custo (R$)</Label>
              <Input
                id="product-cost"
                type="number"
                min={0}
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="product-description">Descrição</Label>
              <Textarea
                id="product-description"
                rows={2}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Opcional — o que está incluso, para quem atende"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Salvando…" : editando ? "Salvar" : "Criar serviço"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
